import { Button } from '@/components/Button';
import { Helmet } from 'react-helmet-async';
import { StripeCheckoutSession } from '@/types/stripeCheckoutSession';
import Footer from '@/ui/Footer';
import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiPostRequest } from '@/utils/apiClient';
import { detectDeviceAndBrowser, getDeviceBrowserLabel } from '@/utils/deviceAndBrowser';
import LoadingForStripe from '@/components/LoadingForStripe';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { Accordion } from '@/components/Accordion';
import { basicPlanPlansFaqItems } from '@/constants/plansFaq';
import { getLocalizedImages } from '@/utils/imageLoader';
import { getAppType } from '@/utils/loginInfo';

// 画像ファイル名のマッピング
const imageFiles = {
    basicplanFirstView: 'basicplan-firstview.gif',
    reportDelivery: 'report-delivery.gif',
    extendMaximumGif: 'ExtendMaximum.gif',
    removeAdsGif: 'RemoveAds.gif',
    previousPeriodComparison: 'previous_period_comparison.jpeg',
    keywordUp: 'keyword_up.jpeg',
    customLayout: 'custom_layout.gif',
    checkIcon: 'check_gold_icon.png',
    fullDisplay: 'full_display.jpeg',
} as const;

function Plans() {
    const [isLoadingForStripe, setIsLoadingForStripe] = useState(false);
    const [sessionContext, setSessionContext] = useState('normal');
    const [searchParams] = useSearchParams();
    const { t, i18n } = useTranslation('plans');
    const hasLoggedPageAccess = useRef(false);

    // 言語に応じた画像パスを取得
    const images = useMemo(() => {
        return getLocalizedImages(imageFiles, i18n.language, 'plans');
    }, [i18n.language]);

    // クエリパラメータ取得とページアクセスログ記録
    useEffect(() => {
        // searchParams変更による再実行を防ぐ（replaceStateでURL変更時の対策）
        if (hasLoggedPageAccess.current) {
            return;
        }
        hasLoggedPageAccess.current = true;

        // リファラーの優先順位: sessionContext > document.referrer
        // replaceStateの前に取得する必要がある
        const sessionContextParam = searchParams.get('sessionContext');
        const fromParam = searchParams.get('from');
        const panelParam = searchParams.get('panel');
        
        // sessionContextをstateに保存（決済セッション作成時に使用）
        if (sessionContextParam) {
            setSessionContext(sessionContextParam);
        }
        
        // URLからパラメータを削除（replaceStateでURL変更時の対策）
        if (sessionContextParam || fromParam || panelParam) {
            window.history.replaceState({}, '', window.location.pathname);
        }

        const logPageAccess = async () => {
            try {
                // リファラーの決定ロジック
                // 1. sessionContextが存在する場合はそれを優先（エラーメッセージからの遷移など）
                // 2. sessionStorageにvalidation_error_sourceが保存されている場合は、それをreferrerとして使用
                // 3. document.referrerが/analysisの場合は、エラーメッセージからの遷移の可能性が高いため
                //    sessionContextがない場合は「analysis_unknown」として記録
                // 4. それ以外の場合はdocument.referrerを使用
                let referrer: string;
                if (sessionContextParam) {
                    referrer = sessionContextParam;
                } else {
                    // sessionStorageからエラーメッセージのソースを取得
                    try {
                        const validationErrorSource = sessionStorage.getItem('validation_error_source');
                        if (validationErrorSource) {
                            referrer = validationErrorSource;
                            // 使用後は削除（1回のみ使用）
                            sessionStorage.removeItem('validation_error_source');
                        } else if (document.referrer && document.referrer.includes('/analysis')) {
                            // /analysisからの遷移でsessionContextもsessionStorageもない場合は、
                            // 通常の分析ページからの遷移として記録
                            referrer = 'analysis_normal';
                        } else {
                            // その他の場合はdocument.referrerを使用
                            referrer = document.referrer || '';
                        }
                    } catch (e) {
                        // sessionStorageが使用できない場合はdocument.referrerを使用
                        referrer = document.referrer || '';
                    }
                }

                const userAgent = navigator.userAgent;

                // 簡易OS判定
                const os = /windows nt/i.test(userAgent)
                    ? 'Windows'
                    : /mac os x/i.test(userAgent)
                        ? 'macOS'
                        : /android/i.test(userAgent)
                            ? 'Android'
                            : /iphone|ipad|ipod/i.test(userAgent)
                                ? 'iOS'
                                : /linux/i.test(userAgent)
                                    ? 'Linux'
                                    : 'Other';

                // デバイス種別判定
                const device = /iphone|ipad|ipod|android|mobile/i.test(userAgent)
                    ? 'mobile'
                    : /tablet/i.test(userAgent)
                        ? 'tablet'
                        : 'desktop';

                // ブラウザ & バージョン判定
                let browser = 'Other';
                let browserVersion = '';
                const edgeMatch = userAgent.match(/Edg\/([\d.]+)/);
                const chromeMatch = userAgent.match(/Chrome\/([\d.]+)/);
                const firefoxMatch = userAgent.match(/Firefox\/([\d.]+)/);
                const safariMatch = !chromeMatch && userAgent.match(/Version\/([\d.]+).*Safari/);

                if (edgeMatch) {
                    browser = 'Edge';
                    browserVersion = edgeMatch[1];
                } else if (chromeMatch) {
                    browser = 'Chrome';
                    browserVersion = chromeMatch[1];
                } else if (firefoxMatch) {
                    browser = 'Firefox';
                    browserVersion = firefoxMatch[1];
                } else if (safariMatch) {
                    browser = 'Safari';
                    browserVersion = safariMatch[1];
                }

                const locale = navigator.language || (navigator as any).userLanguage || '';
                const language = i18n.language;
                const appType = getAppType(); // 'pwa' or 'web'

                const pageAccessData = {
                    pagePath: window.location.pathname,
                    referrer: referrer,
                    metadata: {
                        device,
                        os,
                        browser,
                        browserVersion,
                        language,
                        locale,
                        appType,
                        // トラッキング情報を追加
                        ...(sessionContextParam && { sessionContext: sessionContextParam }),
                        ...(fromParam && { from: fromParam }),
                        ...(panelParam && { panel: panelParam }),
                    }
                };

                await apiPostRequest('/api/log/page-access', pageAccessData);
            } catch (error) {
                // ログ記録の失敗でユーザーに影響を与えないよう、静かに失敗
                console.error('ページアクセスログ記録に失敗しました', error);
            }
        };

        // ページ表示時にログ記録
        logPageAccess();
    }, [searchParams]); // replaceStateによるsearchParams変更時の再実行はrefで防止

    const handleClickTrialButton = async () => {
        try {
            setIsLoadingForStripe(true);
            const requestBody: StripeCheckoutSession = {
                planName: 'basic_plan',
                locale: i18n.language.startsWith('ja') ? 'ja' : 'en',
                campaign: 'none',
                sessionContext: sessionContext,
                deviceType: getDeviceBrowserLabel(detectDeviceAndBrowser()),
            };
            const response = await apiPostRequest(
                '/api/payment/stripe/create-checkout-session',
                requestBody,
            );
            if (response?.ok) {
                const responseData = await response.json();
                setTimeout(() => {
                    window.location.href = responseData.url;
                }, 3000);
            } else if (response?.status === 400) {
                const responseData = await response.json();
                const errorList: string[] = Object.values(responseData.errors).flatMap((val) =>
                    Array.isArray(val) ? val : [val],
                );
                alert(errorList.join('\n'));
                setIsLoadingForStripe(false);
            } else {
                window.location.replace('/login?error=server_error');
            }
        } catch (error) {
            window.location.replace('/login?error=server_error');
        }
    };

    if (isLoadingForStripe) {
        return <LoadingForStripe />;
    }

    // FAQ Items
    const faqItems = basicPlanPlansFaqItems.map((item) => ({
        title: t(item.titleKey),
        content: t(item.contentKey),
    }));

    return (
        <>
            <Helmet>
                <title>{t('meta.title')}</title>
            </Helmet>

            <main className="bg-white text-gray-800">
                {/* Hero Section */}
                <section className="relative overflow-visible bg-gradient-to-tl from-kotomilThemaColor/10 via-kotomilThemaColor/50 to-kotomilThemaColor pb-20 lg:pb-28 pt-4">
                    <div className="container mx-auto px-4 py-6lg:px-8">
                        <div className="w-full text-center relative -mt-8 -mb-4">
                            <span className="block text-yellow-200/80 text-5xl lg:text-9xl font-black tracking-normal transform skew-x-12">
                                Basic Plan
                            </span>
                        </div>
                        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
                            {/* Text Content */}
                            <div className="w-full lg:w-1/2 text-center lg:text-left">
                                <h1 className="text-4xl lg:text-7xl font-bold leading-tight tracking-wide text-gray-800 mb-6">
                                    <span className="leading-tight" dangerouslySetInnerHTML={{ __html: t('firstView.headline.line1') }} />
                                </h1>
                                <p className="text-lg lg:text-2xl tracking-wider text-gray-800 mb-10 max-w-2xl mx-auto lg:mx-0">
                                    <span className="leading-relaxed" dangerouslySetInnerHTML={{ __html: t('firstView.lead') }} />
                                </p>
                                <div className="flex flex-col items-center justify-center gap-4 w-full pt-4">
                                    <Button
                                        variant="original"
                                        className="group relative w-full sm:w-auto sm:min-w-[360px] px-6 sm:px-8 py-4 sm:py-6 text-lg sm:text-2xl lg:text-3xl font-black tracking-normal sm:tracking-widest rounded-full bg-gradient-to-r from-amber-400 via-orange-500 to-red-500 text-white shadow-[0_4px_14px_0_rgba(234,88,12,0.39)] hover:shadow-[0_6px_20px_rgba(234,88,12,0.23)] hover:shadow-orange-500/50 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 overflow-hidden border border-white/20"
                                        onClick={handleClickTrialButton}
                                    >
                                        {/* Top Highlight */}
                                        <div className="absolute top-0 left-0 right-0 h-1/2 bg-gradient-to-b from-white/30 to-transparent rounded-t-full pointer-events-none" />

                                        {/* Shimmer effect */}
                                        <div className="absolute inset-0 -translate-x-full group-hover:animate-[shimmer_1s_infinite] bg-gradient-to-r from-transparent via-white/30 to-transparent z-10" />

                                        <span className="relative z-20 flex items-center justify-center drop-shadow-md gap-3">
                                            {t('firstView.button.try')}
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 opacity-90 group-hover:translate-x-1 transition-transform">
                                                <path fillRule="evenodd" d="M12.97 3.97a.75.75 0 011.06 0l7.5 7.5a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 11-1.06-1.06l6.22-6.22H3a.75.75 0 010-1.5h16.19l-6.22-6.22a.75.75 0 010-1.06z" clipRule="evenodd" />
                                            </svg>
                                        </span>
                                    </Button>
                                    <p className="text-sm text-gray-500 text-center">
                                        {t('firstView.button.cancelNote')}
                                    </p>
                                </div>
                            </div>

                            {/* Image/Visual */}
                            <div className="w-full lg:w-1/2 relative">
                                <div className="relative rounded-2xl shadow-2xl overflow-hidden border-8 border-white bg-gray-100">
                                    <img
                                        src={images.basicplanFirstView}
                                        alt={t('firstView.imageAlt')}
                                        className="w-full h-auto object-cover"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Features Grid */}
                <section className="py-20 bg-gray-50">
                    <div className="container mx-auto px-4 lg:px-8">
                        <div className="text-center max-w-3xl mx-auto mb-16">
                            <h2 className="text-3xl font-bold tracking-wider text-gray-900 sm:text-4xl mb-4">
                                {t('features.heading')}
                            </h2>
                            <p className="text-lg text-gray-600">
                                {t('features.subheading')}
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {[
                                {
                                    number: '01',
                                    hoverColorClass: 'group-hover:text-amber-50',
                                    iconBgClass: 'bg-amber-100',
                                    icon: '📬',
                                    featureKey: 'reportDelivery',
                                    imageSrc: images.reportDelivery,
                                    accentBgClass: 'bg-amber-50/50',
                                    accentBorderClass: 'border-amber-200',
                                },
                                {
                                    number: '02',
                                    hoverColorClass: 'group-hover:text-yellow-50',
                                    iconBgClass: 'bg-yellow-100',
                                    icon: '🔍',
                                    featureKey: 'searchRange',
                                    imageSrc: images.extendMaximumGif,
                                    accentBgClass: 'bg-yellow-50/50',
                                    accentBorderClass: 'border-yellow-200',
                                },
                                {
                                    number: '03',
                                    hoverColorClass: 'group-hover:text-blue-50',
                                    iconBgClass: 'bg-blue-100',
                                    icon: '🖥️',
                                    featureKey: 'fullscreen',
                                    imageSrc: images.fullDisplay,
                                    accentBgClass: 'bg-blue-50/50',
                                    accentBorderClass: 'border-blue-200',
                                },
                                {
                                    number: '04',
                                    hoverColorClass: 'group-hover:text-green-50',
                                    iconBgClass: 'bg-green-100',
                                    icon: '🚫',
                                    featureKey: 'noAds',
                                    imageSrc: images.removeAdsGif,
                                    accentBgClass: 'bg-green-50/50',
                                    accentBorderClass: 'border-green-200',
                                },
                                {
                                    number: '05',
                                    hoverColorClass: 'group-hover:text-purple-50',
                                    iconBgClass: 'bg-purple-100',
                                    icon: '⤴️',
                                    featureKey: 'comparison',
                                    imageSrc: images.previousPeriodComparison,
                                    accentBgClass: 'bg-purple-50/50',
                                    accentBorderClass: 'border-purple-200',
                                },
                                {
                                    number: '06',
                                    hoverColorClass: 'group-hover:text-red-50',
                                    iconBgClass: 'bg-red-100',
                                    icon: '◻️◻️◻️',
                                    featureKey: 'customLayout',
                                    imageSrc: images.customLayout,
                                    accentBgClass: 'bg-red-50/50',
                                    accentBorderClass: 'border-red-200',
                                },
                                {
                                    number: '07',
                                    hoverColorClass: 'group-hover:text-indigo-50',
                                    iconBgClass: 'bg-indigo-100',
                                    icon: '🏷️',
                                    featureKey: 'keywords',
                                    imageSrc: images.keywordUp,
                                    accentBgClass: 'bg-indigo-50/50',
                                    accentBorderClass: 'border-indigo-200',
                                },
                                {
                                    number: '08',
                                    hoverColorClass: 'group-hover:text-emerald-50',
                                    iconBgClass: 'bg-emerald-100',
                                    icon: '📤',
                                    featureKey: 'export',
                                    imageSrc: '',
                                    accentBgClass: 'bg-emerald-50/50',
                                    accentBorderClass: 'border-emerald-200',
                                },
                                {
                                    number: '09',
                                    hoverColorClass: 'group-hover:text-orange-50',
                                    iconBgClass: 'bg-orange-100',
                                    icon: '📅',
                                    featureKey: 'calendarSelection',
                                    imageSrc: '',
                                    accentBgClass: 'bg-orange-50/50',
                                    accentBorderClass: 'border-orange-200',
                                },
                            ].map((feature) => (
                                <div key={feature.featureKey} className={`relative bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow overflow-hidden group flex flex-col ${feature.imageSrc ? 'min-h-[500px]' : ''}`}>
                                    <div className="absolute -top-6 -left-2 pointer-events-none select-none">
                                        <span className={`text-[8rem] font-black text-gray-50 ${feature.hoverColorClass} transition-colors font-mono tracking-tighter leading-none`}>
                                            {feature.number}
                                        </span>
                                    </div>
                                    <div className={`relative z-10 flex flex-col ${feature.imageSrc ? 'flex-1' : ''}`}>
                                        <div className={`w-16 h-16 ${feature.iconBgClass} rounded-lg flex items-center justify-center mb-6 mx-auto`}>
                                            <span className="text-4xl">{feature.icon}</span>
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-3 text-left">
                                            {t(`features.${feature.featureKey}.title`)}
                                        </h3>
                                        <p className="text-gray-600 leading-relaxed mb-6 text-left">
                                            {(() => {
                                                const bullets = t(
                                                    `features.${feature.featureKey}.description1Bullets`,
                                                    { returnObjects: true },
                                                );
                                                const bulletList =
                                                    Array.isArray(bullets) &&
                                                    bullets.length > 0 &&
                                                    typeof bullets[0] === 'string'
                                                        ? (bullets as string[])
                                                        : null;
                                                return bulletList ? (
                                                    <ul
                                                        className={`mb-2 list-disc space-y-1 pb-2 pl-5 font-medium marker:text-gray-500`}
                                                    >
                                                        {bulletList.map((line) => (
                                                            <li key={line}>{line}</li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <span className="mb-2 block pb-2 font-medium">
                                                        {t(`features.${feature.featureKey}.description1`)}
                                                    </span>
                                                );
                                            })()}
                                            <span
                                                className={`block font-semibold text-gray-700 ${feature.accentBgClass} -mx-2 rounded-lg border-l-4 px-3 py-2 ${feature.accentBorderClass} shadow-sm`}
                                            >
                                                {t(`features.${feature.featureKey}.description2`)}
                                            </span>
                                        </p>
                                        {feature.imageSrc && (
                                        <div className="flex-1 flex items-center justify-center">
                                            <div className="rounded-xl overflow-hidden shadow-sm border border-gray-100 w-full">
                                                <img src={feature.imageSrc} alt={t(`features.${feature.featureKey}.imageAlt`)} className="w-full h-auto transform hover:scale-105 transition-transform duration-500" loading="lazy" />
                                            </div>
                                        </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Pricing Comparison */}
                <section className="py-20 bg-white">
                    <div className="container mx-auto px-4 lg:px-8">
                        <div className="text-center max-w-3xl mx-auto mb-16">
                            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl mb-4">
                                {t('pricing.heading')}
                            </h2>
                            <p className="text-lg text-gray-600">
                                {t('pricing.subheading')}
                            </p>
                        </div>

                        <div className="flex flex-col lg:flex-row justify-center gap-8 max-w-5xl mx-auto">
                            {/* Free Plan */}
                            <div className="flex-1 bg-white border border-gray-200 rounded-2xl p-8 lg:p-10 flex flex-col">
                                <div className="mb-4">
                                    <span className="inline-block px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-sm font-medium">
                                        Free
                                    </span>
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('pricing.free.name')}</h3>
                                <p className="text-gray-500 mb-6">{t('pricing.free.tagline')}</p>
                                <div className="text-4xl font-bold text-gray-900 mb-8">
                                    {t('pricing.free.price')} <span className="text-lg text-gray-500 font-normal">{t('pricing.free.priceUnit')}</span>
                                </div>
                                <ul className="space-y-4 mb-8 flex-1">
                                    {(t('pricing.free.features', { returnObjects: true }) as Array<{ text: string; strikethrough: boolean }>).map((feature, index) => (
                                        <li key={index} className={`flex items-center ${feature.strikethrough ? 'text-gray-400 line-through' : 'text-gray-600'}`}>
                                            <span className={`mr-3 ${feature.strikethrough ? '' : 'text-gray-400'}`}>
                                                {feature.strikethrough ? '×' : '✓'}
                                            </span>
                                            {feature.text}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Basic Plan (Highlighted) */}
                            <div className="flex-1 bg-white border-2 border-yellow-400 rounded-2xl p-8 lg:p-10 flex flex-col relative shadow-xl transform lg:-translate-y-4">
                                <div className="absolute top-0 right-0 bg-yellow-400 text-yellow-900 text-xs font-bold px-3 py-1 rounded-bl-xl rounded-tr-lg uppercase tracking-wider">
                                    {t('pricing.basic.recommended')}
                                </div>
                                <div className="mb-4">
                                    <span className="inline-block px-3 py-1 rounded-full bg-yellow-100 text-yellow-800 text-sm font-medium">
                                        {t('comparison.basic.name')}
                                    </span>
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('pricing.basic.name')}</h3>
                                <p className="text-gray-500 mb-6">{t('pricing.basic.tagline')}</p>
                                <div className="text-4xl font-bold text-gray-900 mb-2">
                                    {t('pricing.basic.price')} <span className="text-lg text-gray-500 font-normal">{t('pricing.basic.priceUnit')}</span>
                                </div>
                                <p className="text-sm text-gray-400 mb-8">{t('pricing.basic.priceNote')}</p>
                                <ul className="space-y-4 mb-8 flex-1">
                                    {(t('pricing.basic.features', { returnObjects: true }) as string[]).map((feature, index) => (
                                        <li key={index} className="flex items-center text-gray-800 font-medium">
                                            <img src={images.checkIcon} alt={t('comparison.checkAlt')} className="w-5 h-5 mr-3" />
                                            {feature}
                                        </li>
                                    ))}
                                </ul>
                                <Button
                                    variant="original"
                                    className="group relative w-full px-4 py-4 font-black text-base sm:text-xl rounded-full bg-gradient-to-r from-amber-400 via-orange-500 to-red-500 text-white shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 transition-all duration-300 hover:-translate-y-1 hover:scale-[1.02]"
                                    onClick={handleClickTrialButton}
                                    disabled={isLoadingForStripe}
                                >
                                    {/* Shimmer effect */}
                                    <div className="absolute inset-0 -translate-x-full group-hover:animate-[shimmer_1s_infinite] bg-gradient-to-r from-transparent via-white/30 to-transparent z-10" />
                                    
                                    <span className="relative z-20 flex items-center justify-center gap-2">
                                        {isLoadingForStripe ? (
                                            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto" />
                                        ) : (
                                            <>
                                                {t('pricing.basic.button')}
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 group-hover:translate-x-1 transition-transform">
                                                    <path fillRule="evenodd" d="M16.72 7.72a.75.75 0 011.06 0l3.75 3.75a.75.75 0 010 1.06l-3.75 3.75a.75.75 0 11-1.06-1.06l2.47-2.47H3a.75.75 0 010-1.5h16.19l-2.47-2.47a.75.75 0 010-1.06z" clipRule="evenodd" />
                                                </svg>
                                            </>
                                        )}
                                    </span>
                                </Button>
                                <p className="text-center text-xs text-gray-400 mt-4">
                                    {t('pricing.basic.cancelNote')}
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* FAQ Section */}
                <section className="py-20 bg-gray-50">
                    <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
                        <div className="text-center mb-16">
                            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                                {t('faq.heading')}
                            </h2>
                        </div>
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 lg:p-8">
                            <Accordion
                                items={faqItems}
                                className="space-y-4"
                                titleClassName="text-left font-bold text-gray-800 py-4 hover:text-yellow-600"
                                contentClassName="text-gray-600 pb-4 leading-relaxed pl-4 border-l-2 border-gray-100 ml-2"
                            />
                        </div>
                    </div>
                </section>

                {/* Bottom CTA Area */}
                <section className="relative py-24 lg:py-32 bg-gray-900 overflow-hidden isolate">
                    {/* Background decorations */}
                    <div className="absolute inset-0 -z-10 bg-[radial-gradient(45rem_50rem_at_top,theme(colors.gray.800),theme(colors.gray.900))] opacity-50" />
                    <div className="absolute top-0 right-0 -z-10 transform translate-x-1/3 -translate-y-1/3">
                        <div className="w-[500px] h-[500px] bg-yellow-500/10 rounded-full blur-3xl filter" />
                    </div>
                    <div className="absolute bottom-0 left-0 -z-10 transform -translate-x-1/3 translate-y-1/3">
                        <div className="w-[500px] h-[500px] bg-indigo-500/10 rounded-full blur-3xl filter" />
                    </div>

                    <div className="container mx-auto px-4 lg:px-8 relative z-10">
                        <div className="max-w-4xl mx-auto text-center space-y-8">
                            <h2 className="text-3xl lg:text-5xl font-bold mb-8 tracking-tight text-white leading-tight">
                                {t('cta.heading')}<br className="sm:hidden" />
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 to-yellow-500">
                                    {t('cta.headingHighlight')}
                                </span>
                            </h2>
                            <div className="space-y-4">
                                <p className="text-gray-300 text-lg lg:text-xl leading-relaxed max-w-2xl mx-auto">
                                    {t('cta.description1')}<br className="hidden sm:block" />
                                </p>
                            </div>

                            <div className="flex flex-col items-center justify-center gap-6 w-full">
                                <Button
                                    variant="original"
                                    className="group relative w-full sm:w-auto sm:min-w-[360px] px-6 sm:px-10 py-4 sm:py-8 text-lg sm:text-2xl lg:text-3xl font-black tracking-normal sm:tracking-widest rounded-full bg-gradient-to-r from-amber-400 via-orange-500 to-red-500 text-white shadow-[0_0_40px_-10px_rgba(255,165,0,0.6)] hover:shadow-[0_0_60px_-10px_rgba(255,165,0,0.8)] transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 overflow-hidden border-2 border-white/10 ring-4 ring-orange-500/20"
                                    onClick={handleClickTrialButton}
                                >
                                    {/* Top Highlight */}
                                    <div className="absolute top-0 left-0 right-0 h-1/2 bg-gradient-to-b from-white/20 to-transparent rounded-t-full pointer-events-none" />

                                    {/* Shimmer effect */}
                                    <div className="absolute inset-0 -translate-x-full group-hover:animate-[shimmer_1s_infinite] bg-gradient-to-r from-transparent via-white/30 to-transparent z-10" />

                                    <span className="relative z-20 flex items-center justify-center gap-3 drop-shadow-md">
                                        {t('cta.button')}
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 group-hover:translate-x-1 transition-transform">
                                            <path fillRule="evenodd" d="M12.97 3.97a.75.75 0 011.06 0l7.5 7.5a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 11-1.06-1.06l6.22-6.22H3a.75.75 0 010-1.5h16.19l-6.22-6.22a.75.75 0 010-1.06z" clipRule="evenodd" />
                                        </svg>
                                    </span>
                                </Button>

                                <div className="flex items-center justify-center gap-4 text-xs font-medium text-gray-400 mt-2">
                                    <span className="flex items-center gap-2 bg-gray-800/60 px-4 py-2 rounded-full border border-gray-700/60 backdrop-blur-sm">
                                        <div className="bg-green-500/20 p-1 rounded-full text-green-400">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3">
                                                <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        {t('cta.guarantees.cancelAnytime')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
        </>
    );
}

export default Plans;
